import { memo, useMemo } from 'react';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { formatCurrency } from '../../utils/formatters';

// 10-colour palette — saturated enough to pop on both light and dark surfaces,
// spread across the hue wheel so adjacent slices are always distinguishable.
const PALETTE = [
    '#3b82f6', // blue
    '#10b981', // emerald
    '#f59e0b', // amber
    '#8b5cf6', // violet
    '#ef4444', // red
    '#06b6d4', // cyan
    '#f97316', // orange
    '#ec4899', // pink
    '#84cc16', // lime
    '#6366f1', // indigo
];

const TOOLTIP_STYLE = {
    backgroundColor: 'var(--bs-body-bg)',
    border: '1px solid var(--bs-border-color)',
    borderRadius: '0.5rem',
    color: 'var(--bs-body-color)',
};

export default memo(function JobExpensePieChart({ expenses, currency }) {
    // Group expense amounts by category, then sort largest-first so the
    // dominant slice always starts at the top of the donut.
    const data = useMemo(() => {
        if (!expenses?.length) return [];
        const totals = {};
        for (const exp of expenses) {
            const cat = exp.categoryName?.trim() || 'Uncategorised';
            totals[cat] = (totals[cat] ?? 0) + (exp.amount ?? 0);
        }
        return Object.entries(totals)
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value);
    }, [expenses]);

    if (!data.length) return null;

    return (
        <div className="surface h-100 recharts-outline-none">
            <h3 className="fw-semibold fs-5 mb-3">Expenses by Category</h3>
            <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                    <Pie
                        data={data}
                        cx="50%"
                        cy="50%"
                        innerRadius="40%"
                        outerRadius="65%"
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) =>
                            `${name}: ${(percent * 100).toFixed(0)}%`
                        }
                        labelLine={{ stroke: 'var(--bs-secondary-color)', strokeWidth: 1 }}
                        isAnimationActive={false}
                        fontSize={11}
                    >
                        {data.map((_, i) => (
                            <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                        ))}
                    </Pie>
                    <Tooltip
                        formatter={(v) => formatCurrency(v, currency)}
                        contentStyle={TOOLTIP_STYLE}
                        labelStyle={{ color: 'var(--bs-body-color)' }}
                        itemStyle={{ color: 'var(--bs-body-color)' }}
                    />
                    <Legend
                        layout="horizontal"
                        verticalAlign="bottom"
                        align="center"
                        wrapperStyle={{ fontSize: '0.75rem' }}
                    />
                </PieChart>
            </ResponsiveContainer>
        </div>
    );
});
